import { locationMatchesHint } from './queryParser';

function fieldMatchesTerm(fieldValue, term) {
  const f = String(fieldValue || '').toLowerCase();
  const t = String(term || '').toLowerCase();
  if (!f || !t || t.length < 2) return false;
  return f.includes(t) || t.includes(f);
}

/**
 * Score one experience against a parsed query.
 * Higher = more relevant. Partial matches are encouraged — we never require
 * every part of the query to match.
 *
 * @param {import('./experienceSearch').SearchDocument} doc
 * @param {import('./experienceSearch').ParsedQuery} parsed
 * @returns {{ score: number, reasons: string[] }}
 */
export function scoreSearchDocument(doc, parsed) {
  if (!doc || !parsed.normalized) {
    return { score: 0, reasons: [] };
  }

  let score = 0;
  const reasons = [];
  const { normalized, intentCore, tokens, expandedTerms, constraints } = parsed;

  // Intent core phrase in metadata (after stripping "something", "looking for", etc.)
  const corePhrase = intentCore || normalized;
  if (corePhrase.length >= 3 && doc.haystack.includes(corePhrase)) {
    score += 30;
    reasons.push('intent-core');
  }

  // 1. Exact / strong title matches
  if (doc.titleLower === normalized || doc.titleLower === corePhrase) {
    score += 120;
    reasons.push('exact-title');
  } else if (doc.titleLower.includes(normalized) || doc.titleLower.includes(corePhrase)) {
    score += 65;
    reasons.push('title-phrase');
  } else {
    for (const token of tokens) {
      if (token.length >= 2 && doc.titleLower.includes(token)) {
        score += 18;
        reasons.push('title-token');
      }
    }
  }

  // 2. Structured metadata — themes, audience, group size (catalog knowledge)
  for (const term of expandedTerms) {
    if (term.length < 2 || term === normalized || term === corePhrase) continue;

    for (const theme of doc.themes) {
      if (fieldMatchesTerm(theme, term)) {
        score += 18;
        reasons.push('theme-match');
      }
    }
    for (const bf of doc.bestFor) {
      if (fieldMatchesTerm(bf, term)) {
        score += 16;
        reasons.push('audience-match');
      }
    }
    for (const gs of doc.groupSize) {
      if (fieldMatchesTerm(gs, term)) {
        score += 14;
        reasons.push('group-match');
      }
    }
    if (doc.experience?.multiplayer && ['multiplayer', 'social', 'friends', 'group', 'together'].includes(term)) {
      score += 12;
      reasons.push('multiplayer');
    }
  }

  // 3. Semantic / synonym term matches in full haystack
  let termHits = 0;
  for (const term of expandedTerms) {
    if (term.length < 2 || term === normalized) continue;
    if (doc.haystack.includes(term)) {
      const weight = tokens.includes(term) ? 10 : 6;
      score += weight;
      termHits += 1;
    }
  }
  if (termHits >= 2) {
    score += termHits * 2;
    reasons.push('semantic-cluster');
  }

  // 4. Category hints
  for (const hint of parsed.categoryHints) {
    if (doc.category === hint) {
      score += 22;
      reasons.push('category');
    } else if (doc.haystack.includes(hint.replace(/-/g, ' '))) {
      score += 12;
      reasons.push('category-related');
    }
  }

  // 5. Content type / genre hints
  for (const hint of parsed.contentTypeHints) {
    if (doc.contentType === hint) {
      score += 18;
      reasons.push('genre');
    }
  }

  // 6. Device constraints
  if (constraints.preferNoHeadset) {
    if (doc.hasMobile || doc.hasDesktop) {
      score += 16;
      reasons.push('no-headset-friendly');
    }
    if (doc.isHeadsetOnly) {
      score -= 12;
      reasons.push('headset-only-penalty');
    }
  }

  for (const term of expandedTerms) {
    if (['mobile', 'headset', 'desktop', 'quest', 'phone', 'browser'].includes(term)) {
      if (doc.accessDevices.includes(term) || doc.fields.devices.includes(term)) {
        score += 14;
        reasons.push('device-match');
      }
    }
  }

  // 7. Location
  if (constraints.locationHint && locationMatchesHint(doc, constraints.locationHint)) {
    score += 28;
    reasons.push('location');
  } else {
    for (const token of tokens) {
      if (token.length >= 4 && doc.locationHaystack.includes(token)) {
        score += 12;
        reasons.push('location-token');
      }
    }
  }

  // 8. Mood / vibe
  const moodTerms = expandedTerms.filter((t) =>
    ['horror', 'scary', 'relaxing', 'calm', 'art', 'music', 'comedy', 'funny', 'nature', 'wellness', 'fun', 'entertaining'].includes(t)
  );
  for (const mood of moodTerms) {
    if (doc.themes.some((th) => fieldMatchesTerm(th, mood))) {
      score += 14;
      reasons.push('mood-theme');
    }
    if (doc.bestFor.some((b) => fieldMatchesTerm(b, mood))) {
      score += 10;
      reasons.push('mood-audience');
    }
    if (doc.comfortRating === 'comfortable' && ['relaxing', 'calm', 'peaceful'].includes(mood)) {
      score += 8;
      reasons.push('comfort-match');
    }
    if (doc.comfortRating === 'intense' && ['horror', 'scary', 'thriller'].includes(mood)) {
      score += 8;
      reasons.push('intensity-match');
    }
  }

  // 9. Audience / social occasion
  const audienceTerms = [
    'family', 'families', 'kids', 'children', 'date night', 'solo', 'pairs', 'couples',
    'friends', 'friend', 'social', 'multiplayer', 'group', 'groups', 'together', 'hangout'
  ];
  for (const term of expandedTerms) {
    if (audienceTerms.includes(term)) {
      if (doc.bestFor.some((b) => fieldMatchesTerm(b, term))) {
        score += 14;
        reasons.push('audience');
      }
      if (doc.groupSize.some((g) => fieldMatchesTerm(g, term))) {
        score += 10;
        reasons.push('group-size');
      }
    }
  }

  // 10. Price constraints
  if (constraints.requireFree && doc.isFree) {
    score += 20;
    reasons.push('free');
  }
  if (constraints.maxPriceAmount != null) {
    if (doc.isFree) {
      score += 18;
      reasons.push('within-budget-free');
    } else if (doc.minPrice != null && doc.minPrice <= constraints.maxPriceAmount) {
      score += 14;
      reasons.push('within-budget');
    } else if (doc.minPrice != null && doc.minPrice > constraints.maxPriceAmount) {
      score -= 8;
      reasons.push('over-budget');
    }
  }

  // Token coverage bonus
  const matchedTokens = tokens.filter((t) => t.length >= 2 && doc.haystack.includes(t));
  if (matchedTokens.length >= 1) {
    score += matchedTokens.length * 4;
    reasons.push('token-coverage');
  }

  return { score, reasons };
}

/**
 * Rank experiences by search relevance.
 * If nothing clears the threshold, return best partial matches rather than nothing.
 *
 * @param {import('./experienceSearch').SearchDocument[]} documents
 * @param {import('./experienceSearch').ParsedQuery} parsed
 * @param {{ minScore?: number }} options
 */
export function rankSearchResults(documents, parsed, options = {}) {
  const minScore = options.minScore ?? 4;

  const allScored = documents
    .map((doc) => {
      const { score, reasons } = scoreSearchDocument(doc, parsed);
      return { doc, score, reasons };
    })
    .sort((a, b) => b.score - a.score);

  let scored = allScored.filter((r) => r.score >= minScore);

  // Fallback: user described something real — show best partial matches
  if (scored.length === 0 && parsed.expandedTerms.length > 0) {
    scored = allScored.filter((r) => r.score > 0);
  }

  return scored;
}
